/**
 * timezone.js
 *
 * Central place to figure out the CURRENT USER's timezone.
 * Uses the browser's Intl API to auto-detect the timezone
 * (e.g. "America/New_York" for USA, "Europe/London" for UK,
 * "Europe/Berlin" for Germany, "Asia/Kolkata" for India, etc.)
 *
 * No user input / no country dropdown needed — the browser
 * already knows this based on the OS/system timezone settings.
 *
 * Everywhere in the codebase that used to hardcode "Asia/Kolkata",
 * import { getUserTimeZone } from "utils/timezone" and call
 * getUserTimeZone() instead.
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const FALLBACK_TIMEZONE = "Asia/Kolkata";

/**
 * Returns the IANA timezone string of the current user's browser.
 * Falls back to FALLBACK_TIMEZONE if detection fails for any reason
 * (very old browsers, server-side rendering, etc.)
 *
 * @returns {string} e.g. "America/New_York", "Europe/London", "Europe/Berlin"
 */
export const getUserTimeZone = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || FALLBACK_TIMEZONE;
  } catch (error) {
    console.error("Timezone detection failed, falling back to default:", error);
    return FALLBACK_TIMEZONE;
  }
};

/**
 * Returns a short display label for the user's current timezone,
 * e.g. "GMT+5:30", "GMT-4", "GMT+1" — handy for showing next to
 * dates/times in report tables so users know what TZ they're seeing.
 *
 * @returns {string}
 */
export const getUserTimeZoneOffsetLabel = () => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: getUserTimeZone(),
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find((part) => part.type === "timeZoneName");
    return offsetPart ? offsetPart.value : "";
  } catch (error) {
    console.error("Timezone offset label detection failed:", error);
    return "";
  }
};

export const formatInUserTimeZone = (
  value,
  format = "DD MMM YYYY, hh:mm A",
  sourceIsUtc = true
) => {
  if (!value) return "-";
  try {
    const parsed = sourceIsUtc ? dayjs.utc(value) : dayjs(value);
    if (!parsed.isValid()) return "-";
    return parsed.tz(getUserTimeZone()).format(format);
  } catch (error) {
    console.error("formatInUserTimeZone failed:", error);
    return String(value);
  }
};

export default getUserTimeZone;
